/**
 * AWS Amplify v6 Wrapper
 * 
 * This wrapper provides a stable API interface using:
 * - AWS Amplify Auth for Cognito authentication (sign in, tokens, sessions)
 * - Native fetch() for all REST API calls (bypassing Amplify API module bugs)
 * 
 * Benefits:
 * - Reliable REST API calls without Amplify v6 header timing issues
 * - Automatic authorization header injection
 * - Session caching to prevent race conditions
 * - Maintains backward compatibility with existing code
 */

// AWS Amplify Auth imports only (no API imports needed)
import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resendSignUpCode,
  confirmResetPassword,
  resetPassword,
  signInWithRedirect,
  updateUserAttributes,
  confirmSignIn,
  updatePassword
} from 'aws-amplify/auth';

/**
 * Session cache to minimize fetchAuthSession calls
 * Cache expires after 4 minutes (token is valid for 1 hour, this gives plenty of buffer)
 */
let sessionCache = {
  session: null,
  timestamp: null,
  TTL: 4 * 60 * 1000 // 4 minutes in milliseconds
};

/**
 * Get authentication session with caching
 * Caches the session for 4 minutes to reduce unnecessary fetchAuthSession calls
 * @param {boolean} forceRefresh - Force refresh the session from Cognito
 * @returns {Promise} - Authentication session
 */
async function getAuthSession(forceRefresh = false) {
  try {
    const now = Date.now();

    // Return cached session if valid and not forcing refresh
    if (
      !forceRefresh &&
      sessionCache.session &&
      sessionCache.timestamp &&
      (now - sessionCache.timestamp) < sessionCache.TTL
    ) {
      return sessionCache.session;
    }

    // Fetch new session
    const session = await fetchAuthSession({ forceRefresh });

    // Update cache
    sessionCache.session = session;
    sessionCache.timestamp = now;

    return session;
  } catch (error) {
    console.error('getAuthSession: Failed to fetch session', error);
    // Clear cache on error
    sessionCache.session = null;
    sessionCache.timestamp = null;
    throw error;
  }
}

/**
 * Clear the session cache (useful for sign out or error scenarios)
 */
function clearSessionCache() {
  sessionCache.session = null;
  sessionCache.timestamp = null;
}

/**
 * Build query string from params object
 * @param {object} params - Query parameters
 * @returns {string} - Query string
 */
function buildQueryString(params) {
  if (!params || Object.keys(params).length === 0) return '';

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Get base URL for API name
 * @param {string} apiName - API name (main, secondary)
 * @returns {string} - Base URL
 */
function getBaseURL(apiName) {
  // Both 'main' and 'secondary' use the same base URL
  return process.env.REACT_APP_BASE_URL;
}

// Guards against concurrent callers each independently forcing their own
// Cognito refresh and/or sign-out when several requests hit a dead session
// at the same time.
let isRedirectingToLogin = false;
let inFlightForcedRefresh = null;

function forceRefreshSession() {
  if (!inFlightForcedRefresh) {
    clearSessionCache();
    inFlightForcedRefresh = getAuthSession(true).finally(() => {
      inFlightForcedRefresh = null;
    });
  }
  return inFlightForcedRefresh;
}

async function signOutAndRedirectToLogin() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  clearSessionCache();
  // Don't let a genuinely hung signOut() block the redirect forever — race it
  // against a timeout. signOut() revokes the refresh token over the network
  // BEFORE it clears local tokens, so if we give up too early here we'd
  // navigate away before local cleanup ever runs, leaving stale tokens behind.
  // A real network failure (e.g. offline) rejects within a couple seconds and
  // Amplify already handles that gracefully; this timeout is only a backstop
  // for an actual hang, so it's set generously rather than tightly.
  // The trailing .catch() keeps a late rejection (after the timeout already
  // won the race) from surfacing as an unhandled promise rejection.
  const signOutWithTimeout = Promise.race([
    signOut().catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 10000))
  ]);
  await signOutWithTimeout;
  window.location.href = '/';
}

/**
 * Build request headers, attaching a bearer token from the (optionally
 * force-refreshed) auth session unless the caller already supplied one.
 *
 * `sessionFetchFailed` distinguishes "couldn't fetch a session at all" from
 * "fetched fine, just has no token". Amplify's TokenOrchestrator swallows a
 * revoked/expired refresh token (Cognito's NotAuthorizedException) itself —
 * it resolves with tokens: null rather than throwing — and only rethrows
 * for other failures (principally its own normalized NetworkError). So a
 * thrown error here means the *fetch* failed (network), while a resolved
 * session with no accessToken is the actual signal of a dead refresh token.
 */
async function buildHeaders(baseHeaders, path, forceRefresh) {
  let headers = { ...baseHeaders };
  let sessionFetchFailed = false;

  if (!headers.Authorization) {
    try {
      const session = forceRefresh ? await forceRefreshSession() : await getAuthSession(false);

      if (session?.tokens?.accessToken) {
        headers.Authorization = `Bearer ${session.tokens.accessToken.toString()}`;
      }
    } catch (authError) {
      console.warn('Could not fetch auth session for:', path, authError);
      sessionFetchFailed = true;
    }
  }

  return { headers, sessionFetchFailed };
}

/**
 * Execute a fetch, and on a 401 (e.g. "jwt expired"), invalidate the cached
 * session and retry once with a forced Cognito token refresh. If the retry
 * also 401s — and we actually managed to fetch a session for that retry, so
 * this isn't just a transient network blip — the refresh token itself is no
 * longer valid: sign out and send the user back to login instead of leaving
 * the app silently stuck reusing a dead token on every subsequent call.
 */
async function fetchWithAuthRetry(url, method, baseHeaders, path, body) {
  // A sign-out/redirect is already underway from another call — don't do
  // more work, and don't let this call's error surface (the page is about
  // to navigate away regardless).
  if (isRedirectingToLogin) {
    return new Promise(() => {});
  }

  const doFetch = async (forceRefresh) => {
    const { headers, sessionFetchFailed } = await buildHeaders(baseHeaders, path, forceRefresh);
    const response = await fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });
    return { response, sessionFetchFailed };
  };

  let { response } = await doFetch(false);

  if (response.status === 401) {
    console.warn(`API: got 401 for ${path}, retrying with a forced token refresh`);
    const retry = await doFetch(true);
    response = retry.response;

    if (response.status === 401 && !retry.sessionFetchFailed) {
      console.error(`API: still 401 for ${path} after forced refresh — session is invalid, signing out`);
      await signOutAndRedirectToLogin();
      return new Promise(() => {});
    }
  }

  return response;
}

async function handleResponse(response, isDelete) {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (parseError) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
    error.response = {
      status: response.status,
      data: errorData
    };
    throw error;
  }

  if (isDelete) {
    return { statusCode: response.status };
  }

  return await response.json();
}

function buildUrl(apiName, path, options) {
  const baseURL = getBaseURL(apiName);
  // Support both 'queryParams' (new) and 'queryStringParameters' (Amplify v3 legacy)
  const queryParams = options.queryParams || options.queryStringParameters;
  const queryString = buildQueryString(queryParams);
  return `${baseURL}${path}${queryString}`;
}

const METHODS_WITH_BODY = new Set(['POST', 'PUT', 'PATCH']);

async function request(method, apiName, path, options = {}) {
  try {
    const url = buildUrl(apiName, path, options);
    const headers = METHODS_WITH_BODY.has(method)
      ? { 'Content-Type': 'application/json', ...options.headers }
      : { ...options.headers };
    const body = METHODS_WITH_BODY.has(method) ? (options.body || {}) : undefined;
    const response = await fetchWithAuthRetry(url, method, headers, path, body);
    return await handleResponse(response, method === 'DELETE');
  } catch (error) {
    console.error(`API.${method} error:`, error);
    throw error;
  }
}

/**
 * API Wrapper - uses native fetch with automatic auth headers
 */
export const API = {
  /**
   * GET request
   * @param {string} apiName - API name
   * @param {string} path - API path
   * @param {object} options - Request options (headers, queryParams, etc.)
   * @returns {Promise} - Response promise
   */
  get: (apiName, path, options = {}) => request('GET', apiName, path, options),

  /**
   * POST request
   * @param {string} apiName - API name
   * @param {string} path - API path
   * @param {object} options - Request options (body, headers, etc.)
   * @returns {Promise} - Response promise
   */
  post: (apiName, path, options = {}) => request('POST', apiName, path, options),

  /**
   * PUT request
   * @param {string} apiName - API name
   * @param {string} path - API path
   * @param {object} options - Request options (body, headers, etc.)
   * @returns {Promise} - Response promise
   */
  put: (apiName, path, options = {}) => request('PUT', apiName, path, options),

  /**
   * DELETE request
   * @param {string} apiName - API name
   * @param {string} path - API path
   * @param {object} options - Request options (headers, queryParams, etc.)
   * @returns {Promise} - Response promise
   */
  del: (apiName, path, options = {}) => request('DELETE', apiName, path, options),

  /**
   * PATCH request
   * @param {string} apiName - API name
   * @param {string} path - API path
   * @param {object} options - Request options (body, headers, etc.)
   * @returns {Promise} - Response promise
   */
  patch: (apiName, path, options = {}) => request('PATCH', apiName, path, options)
};

/**
 * Auth Wrapper - maintains the old AWS Amplify v3 Auth interface
 */
export const Auth = {
  /**
   * Sign in user
   * @param {string} username - Username or email
   * @param {string} password - Password
   * @returns {Promise} - Auth result with user attributes (v5 compatible format)
   */
  async signIn(username, password) {
    try {
      // Clear any stale session cache before signing in
      clearSessionCache();

      // Sign out any existing Cognito session from localStorage before attempting sign in.
      // Amplify v6 throws "There is already a signed in user" if a stale session exists.
      try {
        await signOut();
      } catch (_) {
        // No active session to sign out — this is fine, proceed with sign in
      }

      const result = await signIn({ username, password });

      // Force refresh the session to ensure it's available for immediate API calls
      const session = await fetchAuthSession({ forceRefresh: true });

      // Update the cache with the new session
      sessionCache.session = session;
      sessionCache.timestamp = Date.now();

      // Fetch user attributes for backward compatibility with v5 format
      const attributes = await fetchUserAttributes();

      // Return in v5 format with attributes included
      return {
        ...result,
        attributes,
        challengeName: result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
          ? 'NEW_PASSWORD_REQUIRED'
          : undefined
      };
    } catch (error) {
      console.error('Auth.signIn error:', error);
      // Clear cache on error
      clearSessionCache();
      throw error;
    }
  },

  /**
   * Sign up user
   * @param {object} params - Sign up parameters
   * @returns {Promise} - Auth result
   */
  async signUp(params) {
    try {
      const { username, password, attributes, validationData } = params;

      const options = {
        userAttributes: attributes || {}
      };

      // Amplify v6 supports validationData directly in options
      // Convert array format [{Name: 'key', Value: 'val'}] to object format {key: 'val'}
      if (validationData && Array.isArray(validationData)) {
        options.validationData = validationData.reduce((acc, item) => {
          if (item.Name && item.Value !== undefined) {
            acc[item.Name] = item.Value;
          }
          return acc;
        }, {});
      }

      const result = await signUp({
        username,
        password,
        options
      });
      return result;
    } catch (error) {
      console.error('Auth.signUp error:', error);
      // Clear any session cache on signup error
      clearSessionCache();
      throw error;
    }
  },

  /**
   * Confirm sign up
   * @param {string} username - Username
   * @param {string} code - Confirmation code
   * @returns {Promise} - Auth result
   */
  async confirmSignUp(username, code) {
    try {
      const result = await confirmSignUp({ username, confirmationCode: code });
      return result;
    } catch (error) {
      console.error('Auth.confirmSignUp error:', error);
      throw error;
    }
  },

  /**
   * Sign out user
   * @returns {Promise} - Sign out result
   */
  async signOut() {
    try {
      await signOut();
      // Clear the session cache on sign out
      clearSessionCache();
      return 'SUCCESS';
    } catch (error) {
      console.error('Auth.signOut error:', error);
      throw error;
    }
  },

  /**
   * Get current authenticated user
   * @param {object} options - Options (ignored in v6, kept for backward compatibility)
   * @returns {Promise} - Current user with attributes
   */
  async currentAuthenticatedUser(options = {}) {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      // Return in v5 format for backward compatibility
      return {
        username: user.username,
        userId: user.userId,
        signInDetails: user.signInDetails,
        attributes: attributes
      };
    } catch (error) {
      // UserUnAuthenticatedException is expected when no user is logged in — don't spam the console
      if (error.name !== 'UserUnAuthenticatedException') {
        console.error('Auth.currentAuthenticatedUser error:', error);
      }
      throw error;
    }
  },

  /**
   * Get current session
   * @returns {Promise} - Current session
   */
  async currentSession() {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Auth.currentSession error:', error);
      throw error;
    }
  },

  /**
   * Resend sign up code
   * @param {string} username - Username
   * @returns {Promise} - Resend result
   */
  async resendSignUp(username) {
    try {
      const result = await resendSignUpCode({ username });
      return result;
    } catch (error) {
      console.error('Auth.resendSignUp error:', error);
      throw error;
    }
  },

  /**
   * Forgot password
   * @param {string} username - Username
   * @returns {Promise} - Forgot password result
   */
  async forgotPassword(username) {
    try {
      const result = await resetPassword({ username });
      return result;
    } catch (error) {
      console.error('Auth.forgotPassword error:', error);
      throw error;
    }
  },

  /**
   * Forgot password submit
   * @param {string} username - Username
   * @param {string} code - Confirmation code
   * @param {string} new_password - New password
   * @returns {Promise} - Submit result
   */
  async forgotPasswordSubmit(username, code, new_password) {
    try {
      const result = await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: new_password
      });
      return result;
    } catch (error) {
      console.error('Auth.forgotPasswordSubmit error:', error);
      throw error;
    }
  },

  /**
   * Federated sign in with redirect (OAuth/SAML)
   * @param {object} params - Sign in parameters
   * @param {string} params.provider - Identity provider (Google, Facebook, etc.)
   * @param {string} params.customState - Optional custom state to pass along
   * @returns {Promise} - Sign in result (redirects user)
   */
  async federatedSignIn(params) {
    try {
      // Extract provider from params (supports both v5 format and direct object)
      const provider = params.provider || params;

      const result = await signInWithRedirect({
        provider: provider,
        customState: params.customState
      });

      return result;
    } catch (error) {
      console.error('Auth.federatedSignIn error:', error);
      throw error;
    }
  },

  /**
   * Update user attributes
   * @param {object} user - User object (ignored in v6, kept for backward compatibility)
   * @param {object} attributes - Attributes to update
   * @returns {Promise} - Update result
   */
  async updateUserAttributes(user, attributes) {
    try {
      const result = await updateUserAttributes({
        userAttributes: attributes
      });
      return result;
    } catch (error) {
      console.error('Auth.updateUserAttributes error:', error);
      throw error;
    }
  },

  /**
   * Complete new password challenge (for users required to change password on first login)
   * @param {object} user - User object (ignored in v6, kept for backward compatibility)
   * @param {string} password - New password
   * @param {object} requiredAttributes - Required user attributes (e.g., given_name, family_name)
   * @returns {Promise} - Result with user info
   */
  async completeNewPassword(user, password, requiredAttributes = {}) {
    try {
      const result = await confirmSignIn({
        challengeResponse: password,
        options: {
          userAttributes: requiredAttributes
        }
      });

      // Return user info in v5-compatible format
      if (result.isSignedIn) {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        return {
          username: currentUser.username,
          userId: currentUser.userId,
          signInDetails: currentUser.signInDetails,
          attributes: attributes
        };
      }

      return result;
    } catch (error) {
      console.error('Auth.completeNewPassword error:', error);
      throw error;
    }
  },

  /**
   * Change password for authenticated user
   * @param {object} user - User object (ignored in v6, kept for backward compatibility)
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} - Result of password change
   */
  async changePassword(user, oldPassword, newPassword) {
    try {
      await updatePassword({ oldPassword, newPassword });
      return 'SUCCESS';
    } catch (error) {
      console.error('Auth.changePassword error:', error);
      throw error;
    }
  }
};

/**
 * Storage Wrapper - placeholder for future storage functionality
 * Add storage methods here when needed
 */
export const Storage = {
  // Add storage methods as needed
  // get, put, remove, list, etc.
};

// Default export for convenience
const amplifyWrapper = {
  API,
  Auth,
  Storage
};

export default amplifyWrapper;
